// Based on utm-latlng via https://github.com/dnlbaldwin/React-Leaflet-MGRS-Graticule/blob/master/src/Coordinates.js

const a = 6378137.0; //ellip.radius;
const eccSquared = 0.00669438; //ellip.eccsq;

function llToUtm(lat: number, lng: number, resolution = 0) {
	return _LLtoUTM({ lat, lng });
}

function _LLtoUTM(ll: { lat: number; lng: number }) {
	const Lat = ll.lat;
	const Long = ll.lng;

	const k0 = 0.9996;
	const LatRad = degToRad(Lat);
	const LongRad = degToRad(Long);
	let ZoneNumber = Math.floor((Long + 180) / 6) + 1;

	//Make sure the longitude 180.00 is in Zone 60
	if (Long === 180) {
		ZoneNumber = 60;
	}

	// Special zone for Norway
	if (Lat >= 56.0 && Lat < 64.0 && Long >= 3.0 && Long < 12.0) {
		ZoneNumber = 32;
	}

	// Special zones for Svalbard
	if (Lat >= 72.0 && Lat < 84.0) {
		if (Long >= 0.0 && Long < 9.0) {
			ZoneNumber = 31;
		} else if (Long >= 9.0 && Long < 21.0) {
			ZoneNumber = 33;
		} else if (Long >= 21.0 && Long < 33.0) {
			ZoneNumber = 35;
		} else if (Long >= 33.0 && Long < 42.0) {
			ZoneNumber = 37;
		}
	}

	const LongOrigin = (ZoneNumber - 1) * 6 - 180 + 3; //+3 puts origin
	// in middle of
	// zone
	const LongOriginRad = degToRad(LongOrigin);

	const eccPrimeSquared = eccSquared / (1 - eccSquared);

	const N = a / Math.sqrt(1 - eccSquared * Math.sin(LatRad) * Math.sin(LatRad));
	const T = Math.tan(LatRad) * Math.tan(LatRad);
	const C = eccPrimeSquared * Math.cos(LatRad) * Math.cos(LatRad);
	const A = Math.cos(LatRad) * (LongRad - LongOriginRad);

	const M =
		a *
		((1 -
			eccSquared / 4 -
			(3 * eccSquared * eccSquared) / 64 -
			(5 * eccSquared * eccSquared * eccSquared) / 256) *
			LatRad -
			((3 * eccSquared) / 8 +
				(3 * eccSquared * eccSquared) / 32 +
				(45 * eccSquared * eccSquared * eccSquared) / 1024) *
				Math.sin(2 * LatRad) +
			((15 * eccSquared * eccSquared) / 256 + (45 * eccSquared * eccSquared * eccSquared) / 1024) *
				Math.sin(4 * LatRad) -
			((35 * eccSquared * eccSquared * eccSquared) / 3072) * Math.sin(6 * LatRad));

	const UTMEasting =
		k0 *
			N *
			(A +
				((1 - T + C) * A * A * A) / 6.0 +
				((5 - 18 * T + T * T + 72 * C - 58 * eccPrimeSquared) * A * A * A * A * A) / 120.0) +
		500000.0;

	let UTMNorthing =
		k0 *
		(M +
			N *
				Math.tan(LatRad) *
				((A * A) / 2 +
					((5 - T + 9 * C + 4 * C * C) * A * A * A * A) / 24.0 +
					((61 - 58 * T + T * T + 600 * C - 330 * eccPrimeSquared) * A * A * A * A * A * A) /
						720.0));
	if (Lat < 0.0) {
		UTMNorthing += 10000000.0; //10000000 meter offset for
		// southern hemisphere
	}

	return {
		northing: precisionRound(UTMNorthing, 3),
		easting: precisionRound(UTMEasting, 3),
		zoneNumber: ZoneNumber,
		zoneLetter: getLetterDesignator(Lat)
	};
}

function utmToLl(
	UTMEasting: number,
	UTMNorthing: number,
	UTMZoneNumber: number,
	UTMZoneLetter: string
) {
	return _UTMtoLL(UTMEasting, UTMNorthing, UTMZoneNumber, UTMZoneLetter);
}

function _UTMtoLL(
	UTMEasting: number,
	UTMNorthing: number,
	UTMZoneNumber: number,
	UTMZoneLetter: string
) {
	const e1 = (1 - Math.sqrt(1 - eccSquared)) / (1 + Math.sqrt(1 - eccSquared));
	const x = UTMEasting - 500000.0; //remove 500,000 meter offset for longitude
	let y = UTMNorthing;
	const ZoneNumber = UTMZoneNumber;
	const ZoneLetter = UTMZoneLetter;
	let NorthernHemisphere;

	if (['N', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'].indexOf(ZoneLetter) !== -1) {
		NorthernHemisphere = 1;
	} else {
		NorthernHemisphere = 0;
		y -= 10000000.0;
	}

	const LongOrigin = (ZoneNumber - 1) * 6 - 180 + 3;

	const eccPrimeSquared = eccSquared / (1 - eccSquared);

	const M = y / 0.9996;
	const mu =
		M /
		(a *
			(1 -
				eccSquared / 4 -
				(3 * eccSquared * eccSquared) / 64 -
				(5 * eccSquared * eccSquared * eccSquared) / 256));

	const phi1Rad =
		mu +
		((3 * e1) / 2 - (27 * e1 * e1 * e1) / 32) * Math.sin(2 * mu) +
		((21 * e1 * e1) / 16 - (55 * e1 * e1 * e1 * e1) / 32) * Math.sin(4 * mu) +
		((151 * e1 * e1 * e1) / 96) * Math.sin(6 * mu);
	const phi1 = toDegrees(phi1Rad);

	const N1 = a / Math.sqrt(1 - eccSquared * Math.sin(phi1Rad) * Math.sin(phi1Rad));
	const T1 = Math.tan(phi1Rad) * Math.tan(phi1Rad);
	const C1 = eccPrimeSquared * Math.cos(phi1Rad) * Math.cos(phi1Rad);
	const R1 =
		(a * (1 - eccSquared)) / Math.pow(1 - eccSquared * Math.sin(phi1Rad) * Math.sin(phi1Rad), 1.5);
	const D = x / (N1 * 0.9996);

	let Lat =
		phi1Rad -
		((N1 * Math.tan(phi1Rad)) / R1) *
			((D * D) / 2 -
				((5 + 3 * T1 + 10 * C1 - 4 * C1 * C1 - 9 * eccPrimeSquared) * D * D * D * D) / 24 +
				((61 + 90 * T1 + 298 * C1 + 45 * T1 * T1 - 252 * eccPrimeSquared - 3 * C1 * C1) *
					D *
					D *
					D *
					D *
					D *
					D) /
					720);
	Lat = toDegrees(Lat);

	let Long =
		(D -
			((1 + 2 * T1 + C1) * D * D * D) / 6 +
			((5 - 2 * C1 + 28 * T1 - 3 * C1 * C1 + 8 * eccPrimeSquared + 24 * T1 * T1) *
				D *
				D *
				D *
				D *
				D) /
				120) /
		Math.cos(phi1Rad);
	Long = LongOrigin + toDegrees(Long);
	return { lat: Lat, lng: Long };
}

function toDegrees(rad: number) {
	return (rad / Math.PI) * 180;
}

function precisionRound(number: number, precision: number) {
	const factor = Math.pow(10, precision);
	return Math.round(number * factor) / factor;
}
/**
 * Measure distance between two lat/lng points to meters.
 * @param lat1 lat 1
 * @param lon1 lng 1
 * @param lat2 lat 2
 * @param lon2 lng 2
 * @returns Number: Distance in meters
 */
function distanceBetweenTwoLatLngsInMeters(lat1: number, lon1: number, lat2: number, lon2: number) {
	var R = 6378.137; // Radius of earth in KM
	var dLat = (lat2 * Math.PI) / 180 - (lat1 * Math.PI) / 180;
	var dLon = (lon2 * Math.PI) / 180 - (lon1 * Math.PI) / 180;
	var a =
		Math.sin(dLat / 2) * Math.sin(dLat / 2) +
		Math.cos((lat1 * Math.PI) / 180) *
			Math.cos((lat2 * Math.PI) / 180) *
			Math.sin(dLon / 2) *
			Math.sin(dLon / 2);
	var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
	var d = R * c; //kilometers
	return d * 1000; // meters
}

/**
 * Conversion from degrees to radians.
 *
 * @private
 * @param {number} deg the angle in degrees.
 * @return {number} the angle in radians.
 */
function degToRad(deg: number) {
	return deg * (Math.PI / 180.0);
}

/**
 * Calculates the MGRS letter designator for the given latitude.
 *
 * @private
 * @param {number} lat The latitude in WGS84 to get the letter designator
 *     for.
 * @return {char} The letter designator.
 */
function getLetterDesignator(lat: number) {
	//This is here as an error flag to show that the Latitude is
	//outside MGRS limits
	let LetterDesignator = 'Z';

	if (84 >= lat && lat >= 72) {
		LetterDesignator = 'X';
	} else if (72 > lat && lat >= 64) {
		LetterDesignator = 'W';
	} else if (64 > lat && lat >= 56) {
		LetterDesignator = 'V';
	} else if (56 > lat && lat >= 48) {
		LetterDesignator = 'U';
	} else if (48 > lat && lat >= 40) {
		LetterDesignator = 'T';
	} else if (40 > lat && lat >= 32) {
		LetterDesignator = 'S';
	} else if (32 > lat && lat >= 24) {
		LetterDesignator = 'R';
	} else if (24 > lat && lat >= 16) {
		LetterDesignator = 'Q';
	} else if (16 > lat && lat >= 8) {
		LetterDesignator = 'P';
	} else if (8 > lat && lat >= 0) {
		LetterDesignator = 'N';
	} else if (0 > lat && lat >= -8) {
		LetterDesignator = 'M';
	} else if (-8 > lat && lat >= -16) {
		LetterDesignator = 'L';
	} else if (-16 > lat && lat >= -24) {
		LetterDesignator = 'K';
	} else if (-24 > lat && lat >= -32) {
		LetterDesignator = 'J';
	} else if (-32 > lat && lat >= -40) {
		LetterDesignator = 'H';
	} else if (-40 > lat && lat >= -48) {
		LetterDesignator = 'G';
	} else if (-48 > lat && lat >= -56) {
		LetterDesignator = 'F';
	} else if (-56 > lat && lat >= -64) {
		LetterDesignator = 'E';
	} else if (-64 > lat && lat >= -72) {
		LetterDesignator = 'D';
	} else if (-72 > lat && lat >= -80) {
		LetterDesignator = 'C';
	}
	return LetterDesignator;
}

export { llToUtm, utmToLl, distanceBetweenTwoLatLngsInMeters };
